import { FunctionComponent, LazyExoticComponent } from 'react'

import * as flags from './flags'
import * as lazy from './lazy'
import * as paths from './paths'

const BRAND_LITE = 'BRAND_LITE' as const
const BRAND_PRO = 'BRAND_PRO' as const
const RETAILER_LITE = 'RETAIL_LITE' as const
const RETAILER_PRO = 'RETAIL_PRO' as const

export type AccountType =
  | typeof BRAND_LITE
  | typeof BRAND_PRO
  | typeof RETAILER_LITE
  | typeof RETAILER_PRO

export type RouteType = {
  path: string
  element: FunctionComponent | LazyExoticComponent<any>
  title?: string
  feature?: string
  accountTypes?: AccountType[]
}

export const PRIVATE_ROUTES: RouteType[] = [
  {
    path: paths.MOVE_PRODUCTS_PRODUCTS_SELECTION,
    element: lazy.SelectProductsPage,
    title: 'Select Products',
  },
  {
    path: paths.MOVE_PRODUCTS_PO_SELECTION,
    element: lazy.MergeOrdersPage,
    title: 'Move Products',
  },
  {
    path: paths.CONNECTION,
    element: lazy.ConnectionPage,
    title: 'Connection',
    feature: flags.NEW_CONNECTIONS_UI,
  },
  {
    path: paths.DATA_SELF_SERVE,
    element: lazy.DataSelfServePage,
    title: 'Self-serve Data Configuration',
    feature: flags.RETAIL_INTEGRATION_SETTINGS,
  },
  {
    path: paths.DATA_SELF_SERVE_CONFIG,
    element: lazy.DataSelfServeTab,
    title: 'Self-serve Data Configuration',
    feature: flags.RETAIL_INTEGRATION_SETTINGS,
  },
  {
    path: paths.SSO_SETTINGS,
    element: lazy.SsoSettingsPage,
    title: 'SSO Settings',
    feature: flags.SSO_SETTINGS,
  },
  {
    path: paths.ASSORTMENT_REVIEW_UNITS,
    element: lazy.AssortmentReviewPage,
  },
  {
    path: paths.ASSORTMENT_REVIEW,
    element: lazy.AssortmentReviewPage,
  },
  {
    path: paths.ASSORTMENT_REVIEW_EDIT_STYLES,
    element: lazy.AssortmentReviewPage,
  },
  {
    path: paths.ASSORTMENT_REVIEW_EDIT_ATTRIBUTES,
    element: lazy.AssortmentReviewPage,
  },
  {
    path: paths.WORKSPACE_LIST,
    element: lazy.WorkspaceListPage,
  },
  {
    path: paths.ORDER_LIST,
    element: lazy.OrderListPage,
    feature: flags.ORDER_LIST_FLAG,
  },
  {
    path: paths.CANCEL_STYLE,
    element: lazy.CancelStylePage,
  },
  {
    path: paths.ACCOUNTS_HOME,
    element: lazy.BrandDashboardPage,
    feature: flags.BRAND_DASHBOARD,
  },
  {
    path: paths.HOME,
    element: lazy.RetailDashboard,
  },
  {
    path: paths.BRAND_PROFILE,
    element: lazy.LegacyBrandProfileRedirect,
  },
  {
    path: paths.ATTRIBUTE_SETTINGS,
    element: lazy.AttributeSettingsPage,
  },
  {
    path: paths.PRODUCT_CATALOG,
    element: lazy.ProductCatalogPage,
    title: 'Catalog',
  },
  {
    path: paths.PAYMENT_SETTINGS,
    element: lazy.PaymentSettingsPage,
    feature: flags.BRAND_PAYMENTS_SETTINGS,
    title: 'Payments',
  },
  {
    path: paths.PERMISSIONS_SETTINGS,
    element: lazy.PermissionsSettingsPage,
    feature: flags.SELF_SERVE_PERMISSIONING,
    title: 'Permissions',
  },
  {
    path: paths.MANAGE_PERMISSIONS,
    element: lazy.ManagePermissionsPage,
    title: 'Manage Permissions',
    feature: flags.ENABLE_MANAGE_PERMISSIONS,
  },
  {
    path: paths.MANAGE_PERMISSIONS_CONFIGURATION_CREATE,
    element: lazy.ManagePermissionsConfigurationPage,
    title: 'Manage Permissions',
    feature: flags.ENABLE_MANAGE_PERMISSIONS,
  },
  {
    path: paths.MANAGE_PERMISSIONS_CONFIGURATION_EDIT,
    element: lazy.ManagePermissionsConfigurationPage,
    title: 'Manage Permissions',
    feature: flags.ENABLE_MANAGE_PERMISSIONS,
  },
  {
    path: paths.MANAGE_USER_PERMISSIONS,
    element: lazy.UserPermissionsPage,
    title: 'User Permissions',
    feature: flags.ENABLE_MANAGE_PERMISSIONS,
  },
  {
    path: paths.NEW_ORDER_REVIEW,
    element: lazy.NewOrderReviewPage,
    title: 'Order Review',
  },
  {
    path: paths.MANAGE_REGIONS,
    element: lazy.ManageRegionsPage,
    feature: flags.SELF_SERVE_PERMISSIONING,
    title: 'Manage regions',
  },
  {
    path: paths.PASSPORT_QUICK_CONNECT,
    element: lazy.PassportQuickConnect,
    title: 'Passport Quick Connect',
  },
  {
    path: paths.DISCOVERY_DASHBOARD,
    element: lazy.DiscoveryDashboard,
    title: 'Discovery Dashboard',
  },
  {
    path: paths.NEW_SALES_SUMMARY,
    element: lazy.SalesSummaryPage,
    feature: flags.BRAND_REPORTING,
    title: 'Sales Summary',
  },
  {
    path: paths.NEW_STYLE_DETAIL,
    element: lazy.StyleDetailPage,
    feature: flags.BRAND_REPORTING,
    title: 'Style Detail',
  },
  {
    path: paths.NEW_PRODUCTION,
    element: lazy.ProductionPage,
    feature: flags.BRAND_REPORTING,
    title: 'Production',
  },
  {
    path: paths.DELIVERY_REPORT,
    element: lazy.DeliveryReportPage,
    title: 'Delivery Report',
  },
  {
    path: paths.RETAIL_SALES,
    element: lazy.RetailSalesPage,
    title: 'Retail Sales',
  },
  {
    path: paths.CUSTOM_REPORTS,
    element: lazy.CustomReportsPage,
    feature: flags.REPORTING_CONFIGURATIONS,
    title: 'Custom Reports',
  },
  {
    path: paths.CUSTOM_REPORT,
    element: lazy.CustomReportPage,
    feature: flags.REPORTING_CONFIGURATIONS,
    title: 'Custom Report',
  },
  {
    path: paths.ANALYTICS_JOOR_PAY_REPORT_DASHBOARD,
    element: lazy.AnalyticsJoorPayReportPage,
    title: 'JOOR Pay Report Dashboard',
  },
  {
    path: paths.SHOWROOM_TEMPLATE_LIST,
    element: lazy.ShowroomTemplateListPage,
    title: 'The Edit - Templates',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_COLLECTION_ADD,
    element: lazy.ShowroomCollectionPage,
    title: 'The Edit - Collection',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_COLLECTION_VIEW,
    element: lazy.ShowroomCollectionPage,
    title: 'The Edit - Collection',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_COLLECTION_LIST,
    element: lazy.ShowroomCollectionListPage,
    title: 'The Edit - Collections',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_STYLEBOARD_ADD,
    element: lazy.ShowroomStyleboardPage,
    title: 'The Edit - Styleboard',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_STYLEBOARD_VIEW,
    element: lazy.ShowroomStyleboardPage,
    title: 'The Edit - Styleboard',
  },
  {
    path: paths.SHOWROOM_STYLEBOARD_LIST,
    element: lazy.ShowroomStyleboardListPage,
    title: 'The Edit - Styleboards',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.SHOWROOM_MANAGEMENT,
    element: lazy.ShowroomManagementPage,
    title: 'Manage Showrooms',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.STOREFRONT,
    element: lazy.StorefrontPage,
    title: 'Profile',
  },
  {
    path: paths.THIRD_PARTY_SETTINGS,
    element: lazy.ThirdPartySettingsPage,
    title: 'ThirdPartySettingsPage',
    feature: flags.VNTANA,
  },
  {
    path: paths.EXTERNAL_PAYMENTS,
    element: lazy.ExternalPaymentsPage,
    title: 'JOOR Pay External Payments',
  },
  {
    path: paths.LINESHEETS,
    element: lazy.LinesheetsListViewPage,
    title: 'Linesheets',
  },
  {
    path: paths.LINESHEET_DETAIL,
    element: lazy.LinesheetsDetailViewPage,
    title: 'Linesheet Detail',
  },
  {
    path: paths.FIND_NEW_BRANDS,
    element: lazy.FindNewBrandsPage,
    title: 'Find New Brands',
  },
  {
    path: paths.IN_ORDER_ALLOCATION_REPORT,
    element: lazy.InOrderReportPage,
    title: 'Allocation Report',
  },
  {
    path: paths.IN_ORDER_BUDGET_REPORT,
    element: lazy.InOrderReportPage,
    title: 'Budget Report',
  },

  {
    path: paths.EXPORT_TEMPLATES,
    element: lazy.IntegrationSettingsPage,
    title: 'Export Settings',
  },
  {
    path: paths.LIST_JOBS,
    element: lazy.IntegrationSettingsPage,
    title: 'List Jobs',
  },
  {
    path: paths.DATA_ACTIVITY_CENTER,
    element: lazy.DataActivityCenterPage,
    title: 'Data Activity Center',
  },
  {
    path: paths.RETAILER_PROFILE,
    element: lazy.RetailerProfilePage,
  },
  {
    path: paths.STYLES,
    element: lazy.StylesPage,
    title: 'Styles',
  },
  {
    path: paths.STYLES_BULK_IMAGE_UPLOADER,
    element: lazy.BulkImageUploader,
    title: 'Upload Images',
  },
  {
    path: paths.FIND_NEW_RETAILERS,
    element: lazy.FindNewRetailersPage,
    title: 'Find New Retailers',
  },
  {
    path: paths.PROSPECTS,
    element: lazy.ProspectsPage,
    title: 'Prospects',
  },
  {
    path: paths.SUBMISSIONS_CONTACT,
    element: lazy.SubmissionsContactPage,
    title: 'Submission',
    feature: flags.BRAND_SUBMISSIONS_FLAG,
  },
  {
    path: paths.SUBMISSIONS_VIEW,
    element: lazy.SubmissionsViewPage,
    title: 'Submission',
    feature: flags.BRAND_SUBMISSIONS_FLAG,
  },
  {
    path: paths.SUBMISSIONS_LIST,
    element: lazy.SubmissionsListPage,
    title: 'Submissions',
    // add it back after solving bug in PageContent
    // accountTypes: [RETAILER_LITE, RETAILER_PRO],
  },
  {
    path: paths.SHOPIFY_INTEGRATION_CONFIG,
    element: lazy.ShopifyIntegrationConfigRedirect,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_INTEGRATION_TOKEN,
    element: lazy.ShopifyIntegrationTokenRedirect,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_BRAND_SYNC_CONFIG,
    element: lazy.ShopifyBrandConfigPage,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_RETAILER_SYNC_CONFIG,
    element: lazy.ShopifyRetailerConfigPage,
    title: 'Shopify Integration',
  },
  // An unknown :appName falls through to the retailer app.
  {
    path: paths.SHOPIFY_INTEGRATION_CONFIG_COMMON,
    element: lazy.ShopifyIntegrationConfigRedirect,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_BRAND_SYNC_TOKEN,
    element: lazy.ShopifyBrandTokenPage,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_RETAILER_SYNC_TOKEN,
    element: lazy.ShopifyRetailerTokenPage,
    title: 'Shopify Integration',
  },
  // An unknown :appName falls through to the retailer app.
  {
    path: paths.SHOPIFY_INTEGRATION_TOKEN_COMMON,
    element: lazy.ShopifyIntegrationTokenRedirect,
    title: 'Shopify Integration',
  },
  {
    path: paths.ZENDESK_SSO_LOGIN,
    element: lazy.ZendeskSSOLoginPage,
    title: 'Zendesk SSO Login',
  },
  {
    path: paths.SWATCH_MANAGEMENT,
    element: lazy.SwatchManagement,
    title: 'Swatch Management',
    feature: flags.SWATCH_CREATION_AND_MANAGEMENT,
  },
  {
    path: paths.VISUAL_ASSORTMENT,
    element: lazy.VisualAssortmentPage,
    title: 'Visual Assortment',
  },
  {
    path: paths.USER_MANAGEMENT,
    element: lazy.UserManagementPage,
    title: 'User Management',
    feature: flags.SELF_SERVE_USER_MANAGEMENT,
  },
  {
    path: paths.SUBSCRIPTIONS,
    element: lazy.SubscriptionsPage,
    title: 'Subscriptions',
    feature: flags.SELF_SERVE_PREMIUM_FEATURES,
  },
  {
    path: paths.SHIPPING_REPORT,
    element: lazy.ShippingReportPage,
    title: 'Shipping Report',
  },
]

export const PUBLIC_ROUTES: RouteType[] = [
  {
    path: paths.PUBLIC_BRAND_PROFILE,
    element: lazy.LegacyBrandProfileRedirect,
  },
  {
    path: paths.PUBLIC_PRODUCT_CATALOG,
    element: lazy.PublicProductCatalogPage,
    title: 'Catalog',
  },
  {
    path: paths.PUBLIC_INVOICE,
    element: lazy.PublicInvoicePage,
    title: 'Invoice',
  },
  {
    path: paths.PASSPORT_LANDING,
    element: lazy.PassportLandingPage,
    title: 'Passport',
  },
  {
    path: paths.PASSPORT_FAVORITES,
    element: lazy.PassportFavoritesPage,
    title: 'Passport Favorites',
  },
  {
    path: paths.PASSPORT_EVENT,
    element: lazy.PassportEventLandingPage,
    title: 'Passport Event',
  },
  {
    path: paths.PASSPORT_SHOWROOM,
    element: lazy.PassportShowroomPage,
    title: 'Passport Showroom',
  },
  {
    path: paths.INTEGRATION_SETTINGS_PAGE,
    element: lazy.IntegrationSettingsPage,
    title: 'Integration Settings',
  },
  {
    path: paths.SHOWROOM_PUBLIC_COLLECTION_VIEW,
    element: lazy.ShowroomCollectionPage,
    title: 'The Edit - Collection',
    feature: flags.VIRTUAL_SHOWROOM,
  },
  {
    path: paths.PUBLIC_STOREFRONT,
    element: lazy.StorefrontPage,
    title: 'Profile',
  },
  {
    path: paths.SHOPIFY_INTEGRATION,
    element: lazy.ShopifyIntegrationRedirect,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_BRAND_SYNC_INSTALL,
    element: lazy.ShopifyBrandInstallPage,
    title: 'Shopify Integration',
  },
  {
    path: paths.SHOPIFY_RETAILER_SYNC_INSTALL,
    element: lazy.ShopifyRetailerInstallPage,
    title: 'Shopify Integration',
  },
  // An unknown :appName falls through to the retailer app.
  {
    path: paths.SHOPIFY_INTEGRATION_COMMON,
    element: lazy.ShopifyIntegrationRedirect,
    title: 'Shopify Integration',
  },
  // TODO(CLEANUP): REMOVE THIS PATH
  {
    path: paths.PUBLIC_INSTAGRAM_LANDING,
    element: lazy.InstagramLandingPage,
    title: 'JOOR',
  },
  {
    path: paths.PUBLIC_LINESHEET_DETAIL,
    element: lazy.LinesheetsDetailViewPage,
    title: 'Linesheet Detail',
  },
  {
    path: paths.PUBLIC_PAYMENTS_PAGE,
    element: lazy.PublicPaymentsPage,
    title: 'Pay Now',
  },
  {
    path: paths.PUBLIC_RETAILER_PROFILE,
    element: lazy.PublicRetailerProfilePage,
    title: 'Retailer Profile',
  },
]
export const DEFAULT_ROUTES: RouteType[] = [
  // Routes are matched in sequence; put any new /r/ or /ra/ paths
  // before the catch-all error paths, or they will never be reached
  {
    path: paths.PUBLIC_NOT_FOUND,
    element: lazy.ErrorPage,
    title: 'Page Not Found',
  },
  {
    path: paths.AUTHENTICATED_NOT_FOUND,
    element: lazy.ErrorPage,
    title: 'Page Not Found',
  },
]
